import ScoreRing from './table/ScoreRing'
import { startScoreLabel, underlag } from './score'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { Lead } from '@/shared/types'

/** AI assessment is independent of the seller's editable star rating. A lead
 *  added by hand or from an imported list starts at 100 and reads
 *  "100 · Manuell" / "100 · Importerad" until an AI verdict replaces it
 *  (YCRM-41). */
export default function LeadScore({ lead, prominent = false }: {
  lead: Pick<Lead, 'score' | 'scoreAvailable' | 'scoreSource' | 'confidence'>
  prominent?: boolean
}) {
  const available = lead.scoreAvailable === true && lead.score != null
    && Number.isFinite(lead.score) && lead.score >= 0 && lead.score <= 100
  const start = available ? startScoreLabel(lead) : null
  const label = !available ? 'Ej bedömt' : start ? `${lead.score} · ${start}` : `${lead.score}/100`
  const evidence = available ? underlag(lead) : null
  if (!prominent) {
    if (!available) return <span className="text-xs text-ink-500 whitespace-nowrap">Ej bedömt</span>
    if (!start) return <ScoreRing score={lead.score!} size={32} />
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap" aria-label={`Poäng: ${label}`}>
        <ScoreRing score={lead.score!} size={32} />
        <span className="text-[11px] text-ink-500">· {start}</span>
      </span>
    )
  }
  return (
    <div className="flex flex-col items-end" aria-label={`${start ? 'Poäng' : 'AI-poäng'}: ${label}`}>
      <div className="pl-3.5 py-1 min-w-[110px]">
        <span className="block text-[10px] uppercase tracking-widest text-ink-500">{start ? 'Poäng' : 'AI-poäng'}</span>
        {available ? <>
          <div className="font-display text-[44px] font-medium leading-none tracking-tighter text-ink-1000 mt-1">
            {lead.score}<span className="text-sm text-ink-500 ml-0.5 font-normal">{start ? ` · ${start}` : '/100'}</span>
          </div>
          <div className="h-[3px] bg-ink-100 mt-1.5 overflow-hidden" aria-hidden="true">
            <span className="block h-full bg-brand" style={{ width: `${lead.score}%` }} />
          </div>
          {evidence != null && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block mt-1.5 text-[11px] text-ink-500 tabular-nums cursor-default">
                  Underlag {evidence}/100
                </span>
              </TooltipTrigger>
              <TooltipContent>Hur mycket underlag poängen bygger på, inte en sannolikhet</TooltipContent>
            </Tooltip>
          )}
        </> : <div className="text-sm text-ink-500 mt-2">Ej bedömt</div>}
      </div>
    </div>
  )
}
